import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videos, articles, categories } from '@/mocks/data';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const video = useMemo(() => videos.find((v) => v.slug === slug), [slug]);
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedVideos = useMemo(() => {
    if (!video && !article) return [];
    const catSlug = video ? video.category : article ? article.category : '';
    return videos
      .filter((v) => v.slug !== slug && v.category === catSlug)
      .slice(0, 4);
  }, [video, article, slug]);

  const getCategoryName = (catSlug: string) => {
    const cat = categories.find((c) => c.slug === catSlug);
    return cat ? cat.name : '';
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (!video && !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">内容不存在</h1>
          <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isVideo = !!video;
  const content = video || article;
  const canonicalUrl = `https://www.aiqingdao.com/article/${slug}`;
  const pageTitle = content
    ? `${content.title} - 爱情岛视频 | 爱情岛视频在线观看`
    : '爱情岛视频';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={content ? content.description || content.title : '爱情岛视频高清在线观看'}
        keywords={`爱情岛视频,爱情岛视频在线观看,爱情岛视频高清${content && content.tags ? ',' + content.tags.join(',') : ''}`}
        canonicalUrl={canonicalUrl}
        ogType="article"
      />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiqingdao.com/' },
          { '@type': 'ListItem', position: 2, name: content ? content.title : '', item: canonicalUrl },
        ],
      }} />
      {isVideo && video && (
        <SchemaOrg type="Article" data={{
          headline: video.title,
          description: video.description,
          datePublished: video.date,
          url: canonicalUrl,
        }} />
      )}
      {!isVideo && article && (
        <SchemaOrg type="Article" data={{
          headline: article.title,
          description: article.description || article.title,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          url: canonicalUrl,
        }} />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: content ? content.title : '' }]} />

          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-[1000px] mx-auto">
              {isVideo && video && (
                <>
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-background-900 mb-6">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-primary-500/90 rounded-full cursor-pointer hover:bg-primary-600 transition-all duration-300 hover:scale-110">
                        <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                      </div>
                    </div>
                    <span className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 text-white text-sm rounded-md whitespace-nowrap">
                      {video.duration}
                    </span>
                  </div>

                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {video.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {formatViews(video.views)} 次观看
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </div>
                      {video.rating} 分
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </div>
                      {video.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-price-tag-3-line"></i>
                      </div>
                      {getCategoryName(video.category)}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 md:p-6 mb-8">
                    <h2 className="text-base font-semibold text-foreground-900 mb-3">视频简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {video.description}
                    </p>
                  </div>
                </>
              )}

              {!isVideo && article && (
                <>
                  {article.thumbnail && (
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-6">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                    <span>{article.author}</span>
                    <span>{article.date}</span>
                    <span>{formatViews(article.views)} 次阅读</span>
                  </div>

                  {article.tags && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div
                    className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                      prose-h2:text-lg prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                      prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-foreground-900"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </>
              )}

              {relatedVideos.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {relatedVideos.map((v) => (
                      <VideoCard
                        key={v.id}
                        slug={v.slug}
                        title={v.title}
                        thumbnail={v.thumbnail}
                        duration={v.duration}
                        views={v.views}
                        date={v.date}
                        rating={v.rating}
                        category={getCategoryName(v.category)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}